/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import queryString from 'query-string';

export const BASE_URL =
  'https://public-api.wordpress.com/rest/v1.1/sites/153411500';

/**
 * Creates request object and returns a fetch promise.
 *
 * @param {string} endpoint
 * @param {string} method
 * @param {object} body
 *
 * @return {Promise}
 */
function callWordpress(endpoint, method, body) {
  const { Headers, fetch } = window;

  const request = {
    method: method || 'GET',
    credentials: 'same-origin',
    headers: new Headers(),
  };

  request.headers.append('Content-Type', 'application/json');
  request.headers.append('Accept', 'application/json');

  if (body) {
    if (typeof body !== 'object') {
      return new Promise((resolve, reject) =>
        reject('Body must be an object.')
      );
    }

    switch (method) {
      case 'POST':
        request.body = JSON.stringify(body);
        break;

      case 'GET':
        endpoint += `?${queryString.stringify(body)}`;
        break;

      default:
      // no-op
    }
  }

  return fetch(BASE_URL + endpoint, request)
    .then(response => response.json().then(json => ({ json, response })))
    .then(({ json, response }) => {
      if (!response.ok || json.status === 'error') {
        return new Promise((resolve, reject) => reject(json));
      }

      return json;
    })
    .catch(err => Promise.reject(err));
}

/**
 * WORDPRESS symbol.
 *
 * @type {Symbol}
 */
export const WORDPRESS = Symbol('WORDPRESS');

/**
 * Wordpress middleware, wraps action with fetch request. Subsequently triggers actions
 * corresponding to the types passed with the initial action.
 *
 * @return {Promise}
 */
export default () => next => action => {
  const actionData = action[WORDPRESS];

  // Prevent middleware from going past this point if not WORDPRESS is specified.
  if (typeof actionData === 'undefined') {
    return next(action);
  }

  const { endpoint, method, body } = actionData;
  const [requestType, successType, failureType] = actionData.types;

  next({ type: requestType });

  return callWordpress(endpoint, method, body).then(
    response =>
      next({
        type: successType,
        payload: response,
      }),
    errors =>
      next({
        type: failureType,
        payload: errors || 'There was an error.',
      })
  );
};
